/**
 * §8.4 Product Guides — step-by-step walkthroughs with time and
 * prerequisites.
 */

import React from 'react'
import clsx from 'clsx'
import {
  Rocket, Palette, Globe, UsersRound, Wand2, FlaskConical, Bot,
  CreditCard, Cog, Sparkles, Clock, ArrowRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { GUIDES } from '../data'
import { TONE } from '../_shared'

const ICONS = { Rocket, Palette, Globe, UsersRound, Wand2, FlaskConical, Bot, CreditCard, Cog }

const LEVEL_COLOUR = {
  Essential:     { c: 'text-emerald-400', b: 'border-emerald-400/25', bg: 'bg-emerald-400/[0.06]' },
  Beginner:      { c: 'text-sky-400',     b: 'border-sky-400/25',     bg: 'bg-sky-400/[0.06]'    },
  Intermediate:  { c: 'text-brand-gold', b: 'border-brand-gold/30', bg: 'bg-brand-gold/[0.08]' },
  Advanced:      { c: 'text-purple-400',  b: 'border-purple-400/25',  bg: 'bg-purple-400/[0.06]'  },
}

export default function Guides() {
  return (
    <section id="guides" className="relative py-24 md:py-28 scroll-mt-24">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Product Guides</Eyebrow>
          <SectionHeading align="center"
            title={<>Step-by-step walkthroughs{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">for every operator flow.</span></>}
            subtitle="Each guide lists a time estimate, prerequisites and difficulty — so you know exactly what you&apos;re signing up for."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {GUIDES.map((g, i) => {
            const tone = TONE[g.tone] || TONE.orange
            const Icon = ICONS[g.iconKey] || Sparkles
            const lvl = LEVEL_COLOUR[g.level] || LEVEL_COLOUR.Beginner
            return (
              <div key={g.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px] overflow-hidden flex flex-col gap-3">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />

                <div className="relative flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">guide {String(i + 1).padStart(2, '0')}</span>
                </div>

                <div className="relative">
                  <h4 className="text-[15px] font-semibold text-white leading-tight">{g.label}</h4>
                  <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-1.5">{g.body}</p>
                </div>

                <div className="relative flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-zinc-300">
                    <Clock className="h-3 w-3 text-brand-gold" /> {g.mins} min
                  </span>
                  <span className={clsx('inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider', lvl.b, lvl.bg, lvl.c)}>
                    {g.level}
                  </span>
                </div>

                <div className="relative text-[11px] text-zinc-500 leading-relaxed">
                  <span className="font-mono uppercase tracking-wider text-zinc-600 mr-1">Prereq:</span>{g.prereq}
                </div>

                <div className="relative mt-auto pt-1 inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 hover:text-brand-gold transition-colors">
                  Start guide <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
